import React, { useEffect, useState } from "react";
import {
  Button,
  Tooltip,
  Modal,
  CircularProgress,
  Menu,
} from "cprime-foundational-components";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SaveAs from "./SaveAs";
import ManagePageViewSimple from "./ManagePageView/ManagePageViewSimple";
import "./SavePageView.scss";
import UseScreenSize from "../../../shared/useScreenSize";
import { SavedPageResponse } from "../interface";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import {
  setActivePageItem,
  setActivePageTitle,
  setSavedPagesList,
  setViewUpdate,
} from "../../../redux/features/cascadingFilters/savedPagesSlice";
import { axiosInstance } from "../../../utils/axiosInstance";
import { getEnv } from "../../../Constants/env";
import { triggerToast } from "../../../utils/toast";

interface FormData {
  cDashboardName: string;
  is_default: boolean;
  error: string;
}
const getInitialFormData = (): FormData => ({
  cDashboardName: "",
  is_default: false,
  error: "",
});
export interface Dashboard {
  view_name: string;
  createdAt: string;
  updatedAt?: string | null;
  date_created?: string;
  date_updated?: string | null;
  is_pinned: boolean;
  isFav?: boolean;
  is_default?: boolean;
  id: string;
  seqNo?: number;
  userId: number;
  workday?: string | null;
  gridName: string;
  viewData: any;
  viewOrder: number;
  viewBy: string;
  view_order?: number;
}

interface SavePageViewProps {
  viewStatePayload: Record<string, any>;
  onApplySavedView: (
    viewState: Record<string, any>,
    applyViewData?: boolean,
  ) => void;
  hasSharedLink?: boolean;
  onInitResolved?: () => void;
}

interface ReorderMove {
  id: number;
  old_rank: number | null;
  new_rank: number;
}

interface EditingViewState {
  id: number;
  name: string;
  is_default: boolean;
}

const SavePageView: React.FC<SavePageViewProps> = ({
  viewStatePayload,
  onApplySavedView,
  hasSharedLink = false,
  onInitResolved,
}) => {
  const dispatch = useAppDispatch();
  const screenSize = UseScreenSize();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showManagePageViews, setShowManagePageViews] = useState(false);
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [deleteConfirmView, setDeleteConfirmView] = useState<Dashboard | null>(
    null,
  );
  const [formData, setFormData] = useState<FormData>(getInitialFormData);
  const [editingView, setEditingView] = useState<EditingViewState | null>(null);
  const open = Boolean(anchorEl);
  const [load, setLoad] = useState(false);
  const [deleteLoad, setDeleteLoad] = useState(false);

  const isTabletOrMobile = screenSize.width < 945;

  const isBiggerScreen = screenSize.width >= 1200;
  const { isCascadingFilterSwitchChecked = false, isSidebarExpand = false } =
    useAppSelector((state) => state?.toggleFilter ?? {});

  const {
    activePageItem = null,
    activePageTitle = "",
    viewUpdate = false,
    savedPagesList = [],
  } = useAppSelector((state) => state?.savedPages ?? {});

  // const authObject = useContext(AuthContext);
  const [pinnedPages, setPinnedPages] = useState<SavedPageResponse[]>([]);
  const [showSnackbarMsg, setShowSnackbarMsg] = useState(false);

  const getDashboardEnv = () => {
    const rawEnv = (getEnv()?.envtype || "dev").toLowerCase();
    const normalizedEnv = rawEnv === "local" ? "dev" : rawEnv;
    return normalizedEnv.toUpperCase();
  };

  const mapApiViewToSavedView = (view: any): Dashboard => ({
    view_name: view?.name ?? "",
    createdAt: view?.created_at ?? "",
    updatedAt: view?.updated_at ?? null,
    date_created: view?.created_at ?? "",
    date_updated: view?.updated_at ?? null,
    is_pinned: Boolean(view?.is_pinned),
    isFav: Boolean(view?.is_default),
    is_default: Boolean(view?.is_default),
    id: String(view?.id ?? ""),
    seqNo: view?.id,
    userId: 0,
    workday: view?.workday ?? null,
    gridName: "",
    viewData: view?.state ?? {},
    viewOrder: view?.sort_order ?? 0,
    viewBy: "",
    view_order: view?.sort_order ?? 0,
  });

  const applySavedViewState = (
    view: Dashboard,
    applyViewData: boolean = true,
  ) => {
    dispatch(setActivePageItem(view));
    dispatch(setActivePageTitle(view.view_name || ""));
    dispatch(setViewUpdate(false));
    onApplySavedView(view.viewData ?? {}, applyViewData);
  };

  const fetchSavedViews = async () => {
    try {
      const response = await axiosInstance.post(
        "/onboard/dashboard/views",
        {},
        { params: { env: getDashboardEnv() } },
      );

      const apiViews = response?.data?.data?.views ?? [];
      const sortedViews = [...apiViews].sort(
        (a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0),
      );
      const mappedViews = sortedViews.map(mapApiViewToSavedView);
      const defaultView = mappedViews.find((view) => Boolean(view.is_default));

      dispatch(setSavedPagesList(mappedViews));
      setPinnedPages(mappedViews as any);

      // Priority: shared link state (handled in Dashboard) > default view > initial view.
      if (hasSharedLink) {
        return;
      }

      if (defaultView) {
        applySavedViewState(defaultView, false);
      } else if (!mappedViews.length) {
        dispatch(setActivePageItem({}));
        dispatch(setActivePageTitle(""));
        dispatch(setViewUpdate(false));
        onApplySavedView({});
      } else {
        dispatch(setActivePageItem({}));
        dispatch(setActivePageTitle(""));
        dispatch(setViewUpdate(false));
        onApplySavedView({});
      }
    } catch (error) {
      console.error("Error fetching saved dashboard views:", error);
      dispatch(setSavedPagesList([]));
      setPinnedPages([]);
    } finally {
      onInitResolved?.();
    }
  };

  useEffect(() => {
    fetchSavedViews();
  }, [hasSharedLink]);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const resetSaveModalState = () => {
    setShowSaveAs(false);
    setEditingView(null);
    setFormData(getInitialFormData());
  };
  // const handleFetchsavePage = (item: any) => {
  //   handleClose();
  //   UpdatesavedPageListItems(item);
  // };

  const getViewNumericId = (view: any): number | null => {
    const candidate = view?.seqNo ?? view?.id;
    const parsed = Number(candidate);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const applySavedView = (view: Dashboard) => {
    applySavedViewState(view);
    setShowManagePageViews(false);
    handleClose();
  };

  const updateSavedView = async (
    body: Record<string, any>,
    successMessage = "View updated successfully",
  ) => {
    try {
      const response = await axiosInstance.put(
        "/onboard/dashboard/views",
        body,
        { params: { env: getDashboardEnv() } },
      );

      const updatedView = response?.data?.data;
      if (updatedView) {
        dispatch(setActivePageItem(mapApiViewToSavedView(updatedView)));
        if (updatedView?.name) {
          dispatch(setActivePageTitle(updatedView.name));
        }
      }

      await fetchSavedViews();
      triggerToast(successMessage, "success");
      return true;
    } catch (error: any) {
      const statusCode = error?.response?.status;
      if (statusCode === 404) {
        triggerToast(
          "View not found. This view does not exist or you do not have access.",
          "error",
        );
      } else {
        triggerToast("Error unable to update. Please try again.", "error");
      }
      return false;
    }
  };

  const SaveSubmit = async () => {
    const trimmedName = formData.cDashboardName.trim();
    if (!trimmedName) return;

    setLoad(true);
    try {
      const payload = {
        name: trimmedName,
        state: viewStatePayload,
        is_pinned: true,
        is_default: Boolean(formData.is_default),
      };

      const response = await axiosInstance.post(
        "/onboard/dashboard/views/save",
        payload,
        { params: { env: getDashboardEnv() } },
      );

      const createdView = response?.data?.data;
      if (createdView) {
        dispatch(setActivePageItem(mapApiViewToSavedView(createdView)));
        dispatch(setActivePageTitle(createdView?.name ?? trimmedName));
        dispatch(setViewUpdate(false));
      }

      await fetchSavedViews();
      resetSaveModalState();
      triggerToast("View saved successfully", "success");
    } catch (error) {
      console.error("Error saving dashboard view:", error);
      triggerToast("Error unable to save view. Please try again.", "error");
    } finally {
      setLoad(false);
    }
  };

  const SaveUpdate = async () => {
    const viewId = getViewNumericId(activePageItem);
    if (!viewId) {
      triggerToast("No active view. Select a saved view to update.", "error");
      return;
    }

    setLoad(true);
    try {
      const updated = await updateSavedView(
        {
          id: viewId,
          state: viewStatePayload,
        },
        "View updated successfully",
      );

      if (updated) {
        dispatch(setViewUpdate(false));
      }
    } finally {
      setLoad(false);
    }
  };

  const saveEditedView = async () => {
    if (!editingView) return;

    const trimmedName = formData.cDashboardName.trim();
    if (!trimmedName) return;

    setLoad(true);
    try {
      const updated = await updateSavedView(
        {
          id: editingView.id,
          name: trimmedName,
          is_default: Boolean(formData.is_default),
        },
        "View updated successfully",
      );

      if (updated) {
        resetSaveModalState();
      }
    } finally {
      setLoad(false);
    }
  };

  const handleTogglePin = async (view: Dashboard) => {
    const viewId = getViewNumericId(view);
    if (!viewId) return;

    await updateSavedView(
      { id: viewId, is_pinned: !view.is_pinned },
      "View updated successfully",
    );
  };

  const handleToggleDefault = async (view: Dashboard) => {
    const viewId = getViewNumericId(view);
    if (!viewId) return;

    await updateSavedView(
      { id: viewId, is_default: !(view.isFav || false) },
      "View updated successfully",
    );
  };

  const handleRenameView = async (view: Dashboard) => {
    const viewId = getViewNumericId(view);
    if (!viewId) return;

    setEditingView({
      id: viewId,
      name: view.view_name || "",
      is_default: Boolean(view.is_default ?? view.isFav),
    });
    setFormData({
      cDashboardName: view.view_name || "",
      is_default: Boolean(view.is_default ?? view.isFav),
      error: "",
    });
    setShowSaveAs(true);
  };

  const handleDeleteView = async (view: Dashboard) => {
    setDeleteConfirmView(view);
  };

  const confirmDeleteView = async () => {
    if (!deleteConfirmView) return;

    const viewId = getViewNumericId(deleteConfirmView);
    if (!viewId) return;

    try {
      setDeleteLoad(true);
      await axiosInstance.delete("/onboard/dashboard/views", {
        data: { id: viewId },
        params: { env: getDashboardEnv() },
      });

      const activeId = getViewNumericId(activePageItem);
      if (activeId === viewId) {
        dispatch(setActivePageItem({}));
        dispatch(setActivePageTitle(""));
        dispatch(setViewUpdate(false));
      }

      await fetchSavedViews();
      setDeleteConfirmView(null);
      triggerToast("View deleted successfully", "success");
    } catch (error: any) {
      const statusCode = error?.response?.status;
      if (statusCode === 404) {
        triggerToast(
          "View not found. This view does not exist or you do not have access.",
          "error",
        );
      } else {
        triggerToast("Error unable to delete. Please try again.", "error");
      }
    } finally {
      setDeleteLoad(false);
    }
  };

  const handleReorderViews = async (moves: ReorderMove[]) => {
    if (!moves?.length) {
      return false;
    }

    try {
      await axiosInstance.post(
        "/onboard/dashboard/views/reorder",
        { moves },
        { params: { env: getDashboardEnv() } },
      );

      await fetchSavedViews();
      triggerToast("Views reordered successfully", "success");
      return true;
    } catch (error: any) {
      const statusCode = error?.response?.status;

      if (statusCode === 409) {
        triggerToast(
          "View order changed. Saved view order changed by another update. Reloaded latest order.",
          "error",
        );
        await fetchSavedViews();
      } else if (statusCode === 404) {
        triggerToast(
          "View not found. One or more views no longer exist.",
          "error",
        );
        await fetchSavedViews();
      } else {
        triggerToast("Error unable to reorder. Please try again.", "error");
      }

      return false;
    }
  };

  useEffect(() => {
    if (showSnackbarMsg === true) {
      const timer = setTimeout(() => {
        setShowSnackbarMsg(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSnackbarMsg]);

  return (
    <div>
      {isTabletOrMobile ||
      (isCascadingFilterSwitchChecked && !isBiggerScreen) ||
      (isSidebarExpand && !isBiggerScreen) ? (
        <Tooltip
          title={<div>Save Page View</div>}
          disabled={false}
          placement="bottom"
        >
          <button className="dIcons" onClick={handleClick}>
            <SaveOutlinedIcon style={{ height: "24px", width: "24px" }} />{" "}
          </button>
        </Tooltip>
      ) : (
        <Button
          id="demo-customized-button"
          sx={{
            width: "auto",
            height: 40,
            borderRadius: "4px",
            gap: "8px",
            paddingTop: "12px",
            paddingRight: "16px",
            paddingBottom: "12px",
            paddingLeft: "16px",
            borderWidth: "1px",
          }}
          onClick={handleClick}
          variant="tertiary"
          size="small"
          // icon={true}
          endIcon={
            <KeyboardArrowDownOutlinedIcon
              style={{ height: "16px", width: "16px" }}
            />
          }
        >
          {activePageTitle != "" ? (
            <span className={"defaultSaveButton"}>
              {activePageTitle && viewUpdate && <span className="circle" />}
              <span className={"active-label"}>Active:&nbsp;</span>
              <span>{activePageTitle}</span>
            </span>
          ) : (
            "Save Page View"
          )}
        </Button>
      )}
      {/* <Menu
        id="demo-customized-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "demo-customized-button",
          sx: {
            padding: 0, // Remove default padding
          },
        }}
      >
        <div className="menuContainer">
          <div className="scrollableMenu">
            <MenuItem
              disableRipple
              onClick={() => {
                setShowSaveAs(true);
                handleClose();
              }}
            >
              Save as...
            </MenuItem>
            <MenuItem
              onClick={() => {
                SaveUpdate();
                handleClose();
              }}
              disabled={!viewUpdate}
            >
              Save
            </MenuItem>
            <MenuItem onClick={handleClose} disableRipple disabled={true}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <span>CUSTOM PAGE VIEWS</span>
                <span>{`(${pinnedPages?.length})`}</span>
              </div>
            </MenuItem>
            {pinnedPages?.map((page, index) => {
              return (
                <MenuItem
                  onClick={() => {
                    handleFetchsavePage(page);
                  }}
                  disableRipple
                  key={index}
                >
                  {page?.view_name}
                </MenuItem>
              );
            })}
          </div>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={() => {
              setShowManagePageViews(true);
              handleClose();
            }}
            disableRipple
            className="fixedBottomItem"
          >
            Manage Page Views
          </MenuItem>
        </div>
      </Menu> */}

      <Menu
        id="demo-customized-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        groups={[
          {
            items: [
              {
                label: "Save as...",
                onClick: () => {
                  setEditingView(null);
                  setFormData(getInitialFormData());
                  setShowSaveAs(true);
                  handleClose();
                },
                disableRipple: true,
              },
              {
                label: "Save",
                onClick: () => {
                  SaveUpdate();
                  handleClose();
                },
                disabled: !viewUpdate,
              },
              {
                label: `CUSTOM PAGE VIEWS (${pinnedPages?.length})`,
                disabled: true,
                disableRipple: true,
                // isHeader: true, // Optional custom flag if needed in rendering
              },
              ...(pinnedPages?.map((page, index) => ({
                label: page?.view_name,
                onClick: () => applySavedView(page as any),
                key: index,
                disableRipple: true,
              })) || []),
            ],
          },
        ]}
        fixedItems={[
          {
            label: "Manage Page Views",
            onClick: () => {
              setShowManagePageViews(true);
              handleClose();
            },
            disableRipple: true,
          },
        ]}
      />

      <Modal
        onClose={() => {
          setShowManagePageViews(false);
        }}
        title={"Manage Page Views"}
        open={showManagePageViews}
        variant={"passive"}
        slotComponent={
          <ManagePageViewSimple
            savedViews={pinnedPages}
            onApplyView={applySavedView}
            onTogglePin={handleTogglePin}
            onToggleDefault={handleToggleDefault}
            onRenameView={handleRenameView}
            onDeleteView={handleDeleteView}
            onReorderViews={handleReorderViews}
          />
        }
      />

      <Modal
        footer={
          <div className="save-as-footer">
            <hr className="save-as-footer-divider" />
            <div className="save-as-footer-actions">
              <Button
                variant="tertiary"
                onClick={() => {
                  resetSaveModalState();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (editingView) {
                    saveEditedView();
                    return;
                  }
                  SaveSubmit();
                }}
                disabled={formData.cDashboardName == "" || formData.error != ""}
              >
                {load ? (
                  <CircularProgress size="small" />
                ) : editingView ? (
                  "Save"
                ) : (
                  "Save As"
                )}
              </Button>
            </div>
          </div>
        }
        onClose={() => {
          resetSaveModalState();
        }}
        title={editingView ? "Edit Saved View" : "Save"}
        open={showSaveAs}
        variant={"transaction"}
        slotComponent={
          <SaveAs
            formData={formData}
            setFormData={setFormData}
            savedViews={savedPagesList}
            editingViewId={editingView?.id ?? null}
            isEditing={Boolean(editingView)}
          />
        }
        // children={<></>}
      />

      <Modal
        footer={
          <div className="save-as-footer">
            <hr className="save-as-footer-divider" />
            <div className="save-as-footer-actions">
              <Button
                variant="tertiary"
                onClick={() => {
                  setDeleteConfirmView(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  confirmDeleteView();
                }}
              >
                {deleteLoad ? <CircularProgress size="small" /> : "Delete"}
              </Button>
            </div>
          </div>
        }
        onClose={() => {
          setDeleteConfirmView(null);
        }}
        title={"Delete Saved View"}
        open={Boolean(deleteConfirmView)}
        variant={"transaction"}
        slotComponent={
          <div className="saved-page-view delete-confirm-view">
            <div className="description">
              Delete saved view
              {` "${deleteConfirmView?.view_name || "Untitled view"}"?`}
            </div>
          </div>
        }
      />
    </div>
  );
};

export default SavePageView;
